import { addDays, addMinutes, differenceInMinutes } from "date-fns";
import { formatTime, serializeDate } from "@notemeal/utils-date-time";

export const UNDER_HOUR_DURATION_OPTIONS = [15, 30, 45];
export const MAX_HOURS = 24;
export const MAX_DURATION_IN_MINUTES = MAX_HOURS * 60;

export const getDurationOptions = (_startDateTime: Date, currentValue: number) => {
  let durationOptions = UNDER_HOUR_DURATION_OPTIONS.concat(
    // every hour and quarter-hour for up to MAX_HOURS after start (including overnight)
    Array.from({ length: MAX_HOURS }, (_, i) => (i + 1) * 60),
    Array.from({ length: MAX_HOURS }, (_, i) => (i + 1) * 60 + 15),
    Array.from({ length: MAX_HOURS }, (_, i) => (i + 1) * 60 + 30),
    Array.from({ length: MAX_HOURS }, (_, i) => (i + 1) * 60 + 45)
  )
    .filter(dur => dur <= MAX_DURATION_IN_MINUTES)
    .sort((a, b) => a - b);

  // include the current duration if it is not already in the quarter-hour options
  // (e.g. user set an arbitrary time via the clock picker)
  if (currentValue > 0 && !durationOptions.includes(currentValue)) {
    durationOptions.push(currentValue);
  }

  return durationOptions.sort((a, b) => a - b);
};

export const endsOnNextDay = (startDateTime: Date, durationInMinutes: number): boolean => {
  const endDateTime = addMinutes(startDateTime, durationInMinutes);
  return serializeDate(startDateTime) !== serializeDate(endDateTime);
};

export const formatEndTimeLabel = (startDateTime: Date, durationInMinutes: number): string => {
  const endDateTime = addMinutes(startDateTime, durationInMinutes);
  const timeLabel = formatTime(endDateTime, { alwaysShowMinutes: true });
  return endsOnNextDay(startDateTime, durationInMinutes) ? `${timeLabel} (next day)` : timeLabel;
};

/**
 * TimeClock keeps the same calendar day when changing hours/AM-PM.
 * Times at or before the start time are treated as the next day so overnight
 * end times (e.g. 12:30 AM after a 10:00 PM start) can be selected.
 */
export const getDurationInMinutesFromEndClockTime = (endTime: Date | null, startDateTime: Date): number => {
  if (!endTime) {
    return 0;
  }

  let duration = differenceInMinutes(endTime, startDateTime);
  if (duration <= 0) {
    duration = differenceInMinutes(addDays(endTime, 1), startDateTime);
  }

  return duration;
};
